'use client';

/**
 * Stop Session Replay the moment the app navigates to a URL that carries a live
 * token.
 *
 * src/instrumentation-client.ts can only check the INITIAL page load, and this app does
 * client-side navigate to tokenised URLs — verified call sites:
 *   src/app/register/invitation/page.tsx (-> /accept-invitation?token=)
 *   src/app/(full-width-pages)/accept-invitation/page.tsx
 *     (-> /staff-registration?token=, /athlete-registration?token=,
 *         /parent-invitations/accept/<token>)
 *   src/app/parent-invitations/accept/[token]/page.tsx (-> /add-athletes?token=)
 * On those hops Replay is already running, so the init-time gate cannot help:
 * `handleHistorySpanListener` pushes the full destination URL, query string
 * included, into `_context.urls` (@sentry-internal/replay 10.44.0,
 * cjs/index.js:6889) and into the recording payload.
 *
 * MUST NOT FLUSH ON THE WAY OUT. The integration's public `stop()` is
 * `_replay.stop({ forceFlush: recordingMode === 'session' })` (index.js:10033),
 * and a force flush pops `_context.urls` onto the replay_event (index.js:9462,
 * :8190). `replaysSessionSampleRate` is 0 so recording starts in `buffer` mode,
 * but ONE earlier JS error flips `recordingMode` to `'session'`
 * (index.js:8934) — so a parent who hits an error on the dashboard and then
 * clicks through to a token URL would have the public stop() SEND the very URL
 * this component exists to suppress. `_replay.stop({ forceFlush: false })`
 * instead cancels the debounced flush and destroys the event buffer
 * (index.js:8846-8858), so nothing goes out.
 *
 * The public `stop()` stays as a fallback for an SDK whose internals moved. It
 * can flush the CURRENT segment once, which is still strictly better than
 * leaving Replay running: the debounced flush would send that URL anyway, plus
 * everything after it.
 *
 * Stopping is one-way for the rest of the browser session. That is the
 * fail-safe direction.
 *
 * KNOWN LIMITS:
 *  - re-checks on PATHNAME change. Every tokenised navigation above also
 *    changes the pathname. A same-pathname query-only change (adding `?token=`
 *    in place) would not re-fire.
 *  - runs in an effect, i.e. after the router has already pushed the
 *    destination URL into `_context.urls` (index.js:6889). The stop destroys
 *    that buffer, so nothing normally goes out — but a JS error thrown in the
 *    same tick, before this effect, would flush first.
 */

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import * as Sentry from '@sentry/nextjs';
import { isReplayBlockedUrl } from '@/utils/observability/scrubInviteToken';

type ReplayIntegrationLike = {
  stop?: () => unknown;
  _replay?: { stop?: (options: { forceFlush: boolean; reason?: string }) => unknown };
};

/** Exported for the unit test; not part of the component's public surface. */
export function stopReplayWithoutFlushing(integration: ReplayIntegrationLike | undefined): void {
  if (!integration) return;
  const inner = integration._replay;
  if (typeof inner?.stop === 'function') {
    void Promise.resolve(inner.stop({ forceFlush: false, reason: 'cheervue-token-url' })).catch(
      () => {}
    );
    return;
  }
  void Promise.resolve(integration.stop?.()).catch(() => {});
}

export default function SentryReplayGuard() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const { pathname: path, search, hash } = window.location;
    if (!isReplayBlockedUrl(path, search, hash)) return;

    try {
      // 'Replay' is the integration's own `name` field (index.js:9880).
      stopReplayWithoutFlushing(
        Sentry.getClient()?.getIntegrationByName?.('Replay') as ReplayIntegrationLike | undefined
      );
    } catch {
      // Observability must never break the page the customer is on.
    }
  }, [pathname]);

  return null;
}
